import { useAtomCallback } from "jotai/utils";
import { useCallback } from "react";
import { DATA_CHANNEL_MESSAGE_LOGGING_INTERVAL_MS } from "../../config";
import { sendLoggingEvents, TrackingEvents } from "../../events";
import { debounce } from "../../utils";
import type {
  DataChannelAppStateMessage,
  DataChannelMessageSender,
} from "../../video-sdk";
import {
  dataChannelAppStateAtom,
  DataChannelMessageType,
  useSendMessage,
} from "../../video-sdk";

const sendDebouncedLoggingEvents = debounce(
  sendLoggingEvents,
  DATA_CHANNEL_MESSAGE_LOGGING_INTERVAL_MS,
);

export const useSendParticipantStateCallback = () => {
  const getDataTrackState = useAtomCallback(
    useCallback((get) => get(dataChannelAppStateAtom), []),
  );
  const sendMessage = useSendMessage();

  return useCallback(
    async (senderIdentity: DataChannelMessageSender) => {
      const participantState = getDataTrackState();

      const formattedMessage: DataChannelAppStateMessage = {
        participantState,
        sender: senderIdentity,
        type: DataChannelMessageType.APP_STATE,
      };
      const didSendMessage = await sendMessage(formattedMessage);
      if (didSendMessage) {
        sendDebouncedLoggingEvents(
          TrackingEvents.REMOTE_DATA_TRACK_MESSAGE_SENT,
          {
            isRecording: participantState.isRecording,
            patientConsent: participantState.patient.consent,
            providerConsent: participantState.provider.consent,
            isWhiteboardMode: participantState.isWhiteboardMode,
            recordingState: participantState.recordingState,
          },
          {
            logLevel: "info",
            message:
              "Local participant sent a message on the data track (DEBOUNCED)",
          },
        );
      }
    },
    [getDataTrackState, sendMessage],
  );
};
